import { ReactElement, useMemo, useCallback } from 'react';
import { Stack } from '@mui/material';
import { SelectRowProps, TableChangeHandler } from 'react-bootstrap-table-next';
import { useSnackbar } from 'notistack';

import {
  DataTable,
  DialogFeedbackStop,
  DialogDetailsOrder,
  DialogChangeOrderState,
  DialogModifyAssignmentOrders,
  DialogConfimation,
  DialogSetFeedbackStop,
  DialogRebillOrders,
} from '~components/index';
import {
  useAppSelector,
  useDownloadEvidencesItemPDF,
  useLazyRequest,
} from '~hooks/index';
import {
  useDataTableSelectedProps,
  SelectRowsHandlerOnSelectAll,
  SelectRowsHandlerOnSelect,
} from '~hooks/useDataTableSelectedProps';
import { selectAuth } from '~store/slices/auth/index';
import { markAsPendingItems } from '~services/item';
import { ItemExtended } from '~services/item/types';
import { isOnlyReadUser } from '~utils/user';

import { useDashboardVariantV2CarrierContentFinalizedTabContext } from '../../Context';

import { ITEM_KEY_ID } from '../../utils';
import { DashboardVariantV2CarrierContentDataTableContainer } from '../../../../styles';

import { useFinalizedTabDataTableColumns } from './useColumns';
import { CUSTOM_SORT_FIELDS } from './customSortFields';

const DashboardVariantV2CarrierContentFinalizedTabDataTable =
  (): ReactElement => {
    const { enqueueSnackbar } = useSnackbar();

    const { user } = useAppSelector(selectAuth);

    const isUserOnlyReader = useMemo(() => isOnlyReadUser(user), [user]);

    const {
      selection: { selectedIds, utils: selectedIdsUtils },
      table: {
        data,
        loading: loadingRequestTable,
        totalSize,
        queryParams: { pagination },
        handleChangeTable: changeTable,
      },
      actions: { reloadDataAndResetSelectedRows, handleShowSuccessAlert },
      dialogs: {
        feedback: {
          open: openDialogFeedback,
          data: selectedDataForFeedback,
          handleClose: handleCloseDialogFeedback,
        },
        detailsOrder: {
          open: openDialogDetailsOrder,
          data: selectedDataForDetailsOrder,
          handleClose: handleCloseDialogDetailsOrder,
        },
        changeStateOrder: {
          open: openDialogChangeStateOrder,
          data: selectedOrderDataToChangeState,
          handleClose: handleCloseDialogChangeStateOrder,
        },
        modifyAssignmentOrder: {
          open: openDialogModifyAssignmentOrder,
          data: selectedItemIdToModifyAssignment,
          handleClose: handleCloseDialogModifyAssignmentOrder,
        },
        markAsPending: {
          open: openDialogMarkAsPendingSelectedOrder,
          data: selectedItemMarkAsPendingOrder,
          handleClose: handleCloseDialogConfirmMarkAsPendingSelectedOrder,
        },
        setFeedbackStop: {
          open: openDialogSetFeedbackStop,
          data: selectedItemSetFeedbackStop,
          handleClose: handleCloseDialogSetFeedbackStop,
        },
        rebillOrder: {
          open: openDialogRebillOrder,
          data: selectedItemIdToRebill,
          handleClose: handleCloseDialogRebillOrder,
        },
      },
    } = useDashboardVariantV2CarrierContentFinalizedTabContext();

    const {
      loading: loadingDowloadEvidencesOrderPDF,
      onDownloadPDF: handleDowloadEvidencesOrderPDF,
    } = useDownloadEvidencesItemPDF();

    const columns = useFinalizedTabDataTableColumns({
      loadingDowloadEvidencesOrderPDF,
      onDowloadEvidencesOrderPDF: handleDowloadEvidencesOrderPDF,
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleChangeTable = useCallback<TableChangeHandler<any>>(
      (type, newState) => {
        const sortField =
          CUSTOM_SORT_FIELDS[newState?.sortField] ?? newState?.sortField;

        changeTable(type, { pagination: { ...newState, sortField } });
      },
      [changeTable],
    );

    const handleOnSelectAll = useCallback<
      SelectRowsHandlerOnSelectAll<ItemExtended>
    >(
      (isSelected, rows) => {
        if (isSelected) {
          const ids = rows.map((row) => row[ITEM_KEY_ID]);
          selectedIdsUtils.addMultiple(ids);
        } else {
          selectedIdsUtils.reset();
        }
      },
      [selectedIdsUtils],
    );

    const handleOnSelect = useCallback<SelectRowsHandlerOnSelect<ItemExtended>>(
      (row) => {
        const currentId = row[ITEM_KEY_ID];
        selectedIdsUtils.toggle(currentId);
      },
      [selectedIdsUtils],
    );

    const selectRowsProps = useDataTableSelectedProps({
      onSelectAll: handleOnSelectAll,
      onSelect: handleOnSelect,
      selectedIds,
    });

    const dataTableSelectRowProps = useMemo<
      SelectRowProps<ItemExtended> | undefined
    >(
      () => (isUserOnlyReader ? undefined : selectRowsProps),
      [isUserOnlyReader, selectRowsProps],
    );

    const messageDialogConfimationMarkAsPendingSelectedOrder = useMemo(
      () =>
        `El pedido ${selectedItemMarkAsPendingOrder?.trackingCode} será marcado como pendiente`,
      [selectedItemMarkAsPendingOrder],
    );

    const [
      ,
      loadingMarkAsPendingSelectedOrder,
      ,
      executeMarkAsPendingSelectedOrder,
    ] = useLazyRequest({
      request: markAsPendingItems,
      withPostSuccess: () => {
        const currentTrackingCode =
          selectedItemMarkAsPendingOrder?.trackingCode;

        handleShowSuccessAlert(
          'Destino marcado como pendiente correctamente',
          // eslint-disable-next-line max-len
          `El destino ${currentTrackingCode} ahora está disponible en la sección de pedidos pendientes.`,
        );

        handleCloseDialogConfirmMarkAsPendingSelectedOrder();

        reloadDataAndResetSelectedRows();
      },
      withPostFailure: () => {
        enqueueSnackbar(
          'No se pudo marcar el pedido como pendiente, intente nuevamente',
          { variant: 'error' },
        );
      },
    });

    const handleMarkAsPendingSelectedOrder = useCallback(() => {
      if (selectedItemMarkAsPendingOrder) {
        executeMarkAsPendingSelectedOrder([
          selectedItemMarkAsPendingOrder.itemId,
        ]);
      }
    }, [executeMarkAsPendingSelectedOrder, selectedItemMarkAsPendingOrder]);

    const handleSuccessChangeOrdersState = useCallback(() => {
      handleCloseDialogChangeStateOrder();

      reloadDataAndResetSelectedRows();
    }, [reloadDataAndResetSelectedRows, handleCloseDialogChangeStateOrder]);

    return (
      <>
        <Stack>
          <DashboardVariantV2CarrierContentDataTableContainer fullWidth>
            <DataTable
              loading={loadingRequestTable}
              data={data}
              columns={columns}
              keyField={ITEM_KEY_ID}
              pagination={{
                options: {
                  sizePerPage: pagination.sizePerPage,
                  page: pagination.page,
                  totalSize,
                },
              }}
              remote={{
                pagination: true,
              }}
              onTableChange={handleChangeTable}
              selectRow={dataTableSelectRowProps}
              hideableStorageKey="Dashboard-Carrier-Finalized-Data-Table"
              toggleHideableColumns
              excludeHideableFieldColumns={['title', 'itemStateTypeId']}
            />
          </DashboardVariantV2CarrierContentDataTableContainer>
        </Stack>

        {openDialogFeedback && selectedDataForFeedback && (
          <DialogFeedbackStop
            open={openDialogFeedback}
            onClose={handleCloseDialogFeedback}
            enabledFeebackPanel
            {...selectedDataForFeedback}
          />
        )}

        {openDialogDetailsOrder && selectedDataForDetailsOrder && (
          <DialogDetailsOrder
            open={openDialogDetailsOrder}
            onClose={handleCloseDialogDetailsOrder}
            enabledChipTitle
            {...selectedDataForDetailsOrder}
          />
        )}

        {openDialogChangeStateOrder && selectedOrderDataToChangeState && (
          <DialogChangeOrderState
            open={openDialogChangeStateOrder}
            onClose={handleCloseDialogChangeStateOrder}
            {...selectedOrderDataToChangeState}
            onChangeSuccess={handleSuccessChangeOrdersState}
          />
        )}

        {openDialogModifyAssignmentOrder &&
          selectedItemIdToModifyAssignment && (
            <DialogModifyAssignmentOrders
              open={openDialogModifyAssignmentOrder}
              onClose={() => handleCloseDialogModifyAssignmentOrder()}
              itemIds={[selectedItemIdToModifyAssignment]}
              onSuccessModify={() => reloadDataAndResetSelectedRows()}
            />
          )}

        <DialogConfimation
          open={openDialogMarkAsPendingSelectedOrder}
          description={messageDialogConfimationMarkAsPendingSelectedOrder}
          onConfirm={handleMarkAsPendingSelectedOrder}
          onCancel={handleCloseDialogConfirmMarkAsPendingSelectedOrder}
          loadingConfirm={loadingMarkAsPendingSelectedOrder}
        />

        {openDialogSetFeedbackStop && selectedItemSetFeedbackStop && (
          <DialogSetFeedbackStop
            open={openDialogSetFeedbackStop}
            onClose={handleCloseDialogSetFeedbackStop}
            routeItemId={selectedItemSetFeedbackStop.routeItemId ?? ''}
            routeId={selectedItemSetFeedbackStop.routeId ?? ''}
          />
        )}

        {openDialogRebillOrder && selectedItemIdToRebill && (
          <DialogRebillOrders
            open={openDialogRebillOrder}
            onClose={() => handleCloseDialogRebillOrder()}
            itemIds={[selectedItemIdToRebill]}
            onSuccessSubmit={() => reloadDataAndResetSelectedRows()}
            excludeMode="manual"
          />
        )}
      </>
    );
  };

export default DashboardVariantV2CarrierContentFinalizedTabDataTable;
